/* Disposition Reporting -- frontend API module
 * Backend endpoints:
 *   POST /api/get_disposition_report     -- list view, ADMIN-only (Phase 1)
 *   POST /api/get_disposition_package    -- package view, ADMIN-only (Phase 1)
 *   POST /api/generate_disposition_xlsx  -- binary .xlsx OR JSON error envelope
 *                                           (Phase 3a-i Stellantis, 3a-ii Nissan)
 *   POST /api/generate_site_status_pdf   -- binary .pdf OR JSON error envelope
 *                                           (Phase 5b-i)
 */

import { apiFetch } from '../../../auth/apiFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

/**
 * Fetch the disposition report envelope for a site / OEM filter.
 * Server-side sort is applied per Phase 1 §1b.4 (gap_level → -gap_score → supplier_tool_id).
 * Do NOT re-sort client-side.
 *
 * @param {Object} args
 * @param {string} args.siteId       optional; required when oem is omitted
 * @param {string} args.oem          optional; uppercase enum
 * @param {string} args.callerEmail  required; ADMIN-role caller
 * @returns {Promise<Object>}        envelope: { total_count, submittable_count, pending_count, tools[] }
 */
export async function getDispositionReport({ siteId, oem, callerEmail, toolIds, dwellOverrideToolIds }) {
  const body = { caller_email: callerEmail };
  if (siteId) body.site_id = siteId;
  if (oem) body.oem = oem;
  // Engine 2 handoff: tool_ids selects cross-site (home≠storage); dwell_override
  // flips submittability for the dwell-crossed subset. Both ride the body.
  if (Array.isArray(toolIds) && toolIds.length) body.tool_ids = toolIds;
  if (Array.isArray(dwellOverrideToolIds) && dwellOverrideToolIds.length) body.dwell_override_tool_ids = dwellOverrideToolIds;

  const res = await apiFetch(`${API_BASE_URL}/get_disposition_report`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.error || `Disposition report failed (${res.status})`);
  }
  return res.json();
}

/**
 * Generate the OEM disposition .xlsx for a site/OEM.
 *
 * Returns the raw Response so the caller can inspect headers (specifically
 * Content-Type and Content-Disposition) and branch:
 *   - application/json  -> empty-submittable / OEM-not-supported / error envelope
 *   - application/vnd.openxmlformats-... -> binary .xlsx for blob download
 *
 * Backend may return HTTP 200 with a JSON envelope on the empty-submittable
 * and OEM-not-supported paths -- those are not errors. Only HTTP non-200
 * (after the AdminRoute guard, only token-expiry mid-session is realistic)
 * indicates a true failure.
 *
 * @param {Object} args
 * @param {string} args.siteId       required
 * @param {string} args.oem          required, uppercase enum
 * @param {string} args.callerEmail  required, ADMIN-role caller
 * @returns {Promise<Response>}
 */
export async function generateDispositionXlsx({ siteId, oem, callerEmail, toolIds, dwellOverrideToolIds }) {
  const body = { caller_email: callerEmail, oem };
  if (siteId) body.site_id = siteId;
  if (Array.isArray(toolIds) && toolIds.length) body.tool_ids = toolIds;
  if (Array.isArray(dwellOverrideToolIds) && dwellOverrideToolIds.length) body.dwell_override_tool_ids = dwellOverrideToolIds;
  return apiFetch(`${API_BASE_URL}/generate_disposition_xlsx`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Generate the Site Status PDF for a site/OEM/gaps_only filter combo.
 *
 * Mirrors generateDispositionXlsx's contract: returns the raw Response so
 * the caller can inspect headers (specifically Content-Type and
 * Content-Disposition) and branch:
 *   - application/json -> SITE_NOT_FOUND, GENERATION_FAILED, or one of the
 *                         four picker validation envelopes (unknown_fields,
 *                         missing_mandatory_field, too_many_fields,
 *                         width_overflow)
 *   - application/pdf  -> binary .pdf for blob download (includes the
 *                         single-page empty-state PDF when the filter
 *                         combo returns zero tools)
 *
 * Phase 5b-i shipped the backend; Phase 1 of the picker added the optional
 * fields[] + include_photos body params. Both are omitted from the request
 * body when not provided, preserving byte-identical default behavior for
 * callers that don't use the picker.
 *
 * @param {Object} args
 * @param {string} args.siteId        required, partition key for Sites
 * @param {string} args.oem           optional, uppercase enum or empty
 * @param {boolean} args.gapsOnly     optional, mirrors the on-screen toggle
 * @param {string} args.callerEmail   required, ADMIN-role caller
 * @param {string[]} args.fields      optional, picker selection (must
 *                                    include 'tool_id_display' if present)
 * @param {boolean} args.includePhotos optional, picker photos toggle
 * @returns {Promise<Response>}
 */
export async function generateSiteStatusPdf({
  siteId,
  oem,
  gapsOnly,
  callerEmail,
  fields,
  includePhotos,
}) {
  const body = {
    caller_email: callerEmail,
    site_id: siteId,
    oem,
    gaps_only: !!gapsOnly,
  };
  if (Array.isArray(fields) && fields.length > 0) body.fields = fields;
  if (typeof includePhotos === 'boolean') body.include_photos = includePhotos;

  return apiFetch(`${API_BASE_URL}/generate_site_status_pdf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}
